import { memo, useState, useRef, useEffect, useCallback } from 'react'
import { PencilSquareIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/AuthContext'
import { useEntityReviews } from '../../hooks/useEntityReviews'
import { useReviewForm } from '../../hooks/useReviewForm'
import StarRating from './StarRating'
import ReviewForm from './ReviewForm'
import ReviewList from './ReviewList'

/**
 * EntityReviewsSection - Reviews and ratings for an entity.
 * Renders inline (no accordion) — intended for use inside the Reviews tab.
 * @idempotent @atomic
 */
const EntityReviewsSection = memo(function EntityReviewsSection({ entityId, autoOpenReview = false, t }) {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = !!user?.isAdmin
  const [showForm, setShowForm] = useState(false)

  const { reviews, loading, hasMore, total, avgRating, loadMore, refetch } =
    useEntityReviews(entityId)

  const {
    rating, setRating,
    content, setContent,
    editingReview, startEditing, cancelEditing,
    submit, deleteReview,
    loading: formLoading
  } = useReviewForm({ entityId, onSuccess: refetch, t })

  const formRef = useRef(null)

  // Force form open when editing starts
  useEffect(() => {
    if (editingReview) setShowForm(true)
  }, [editingReview])

  // Auto-open write form when navigated here via enrichment nudge
  useEffect(() => {
    if (autoOpenReview && isAuthenticated) setShowForm(true)
  }, [autoOpenReview, isAuthenticated])

  // Scroll form into view when it opens
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [showForm])

  const handleToggleForm = useCallback(() => setShowForm(p => !p), [])
  const handleCancel = useCallback(() => { cancelEditing(); setShowForm(false) }, [cancelEditing])

  return (
    <div className="mb-3">

      {/* Summary header — avg rating + write-a-review CTA */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {avgRating != null
            ? <>
                <span className="text-2xl font-bold text-gray-900 leading-none">
                  {Number(avgRating).toFixed(1)}
                </span>
                <div className="flex flex-col gap-0.5">
                  <StarRating rating={Math.round(avgRating)} readonly size="sm" />
                  {total > 0 && (
                    <span className="text-[11px] text-gray-400">
                      {total} {t('review.title', 'reviews')}
                    </span>
                  )}
                </div>
              </>
            : (
              <span className="text-xs text-gray-400 italic">
                {t('review.noRatingsYet', 'No ratings yet')}
              </span>
            )
          }
        </div>

        {isAuthenticated && !editingReview && (
          <button
            type="button"
            onClick={handleToggleForm}
            onTouchEnd={(e) => { e.preventDefault(); handleToggleForm() }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              showForm
                ? 'bg-gray-100 text-gray-500'
                : 'bg-primary/10 text-primary hover:bg-primary/20'
            }`}
          >
            <PencilSquareIcon className="w-3.5 h-3.5" />
            {showForm
              ? t('common.cancel', 'Cancel')
              : t('review.writeReview', 'Write a review')
            }
          </button>
        )}

        {!isAuthenticated && (
          <span className="text-xs text-gray-400 italic">
            {t('review.signInToReview', 'Sign in to review')}
          </span>
        )}
      </div>

      {/* Write / edit form — toggled by header CTA or edit action */}
      {showForm && isAuthenticated && (
        <div ref={formRef} className="mb-4">
          {editingReview && isAdmin && editingReview.userId !== user?.id && editingReview.user_id !== user?.id && (
            <p className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
              {t('review.editingAsAdmin', 'Editing as admin')}
            </p>
          )}
          <ReviewForm
            rating={rating}
            setRating={setRating}
            content={content}
            setContent={setContent}
            editingReview={editingReview}
            onSubmit={submit}
            onCancel={handleCancel}
            loading={formLoading}
            t={t}
          />
        </div>
      )}

      {/* Review list */}
      <ReviewList
        reviews={reviews}
        hasMore={hasMore}
        loading={loading}
        onLoadMore={loadMore}
        currentUserId={user?.id}
        isAdmin={isAdmin}
        onEdit={startEditing}
        onDelete={deleteReview}
        t={t}
      />
    </div>
  )
})

export default EntityReviewsSection
